import React, { useState } from "react";
import { X, Upload, FileVideo, FileImage } from "lucide-react";
import { Button } from "ui/components/button";
import { Card } from "ui/components/card";

/**
 * MultiFileUpload - Presentational Component
 * Handles UI display and user interactions for multiple file uploads
 *
 * @param {Array} value - Array of file objects with id, name, type, url, isImage, isVideo
 * @param {Function} onChange - Callback with array of file objects when files are added/removed
 * @param {string} accept - File types to accept (default: "image/*,video/*")
 * @param {number} maxFiles - Maximum number of files allowed (default: 10)
 * @param {boolean} disabled - Disable interactions
 * @param {string} className - Additional CSS classes
 */
const MultiFileUpload = ({
  value = [],
  onChange,
  disabled = false,
  accept = "image/*,video/*",
  maxFiles = 10,
  className = "",
}) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleNewFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleNewFiles(e.target.files);
    }
  };

  const handleNewFiles = (fileList) => {

    if (disabled) return;

    const fileArray = Array.from(fileList);

    if (value.length + fileArray.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Create file objects with preview URLs
    const newFileObjects = fileArray.map((file) => ({
      file: file,
      name: file.name,
      type: file.type,
      preview: URL.createObjectURL(file),
      isImage: file.type.startsWith("image/"),
      isVideo: file.type.startsWith("video/"),
      isNew: true,
    }));

    onChange?.([...value, ...newFileObjects]);
  };

  const removeFile = (index) => {
    if (disabled) return;

    const fileToRemove = value[index];

    // Revoke object URL to prevent memory leaks
    if (fileToRemove.preview && fileToRemove.isNew) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    const updatedFiles = value.filter((_, i) => i !== index);
    onChange?.(updatedFiles);
  };

  return (
    <div className={className}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
          ? "border-blue-500 bg-blue-50"
          : "border-gray-300 hover:border-gray-400"
          } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept={accept}
          onChange={handleFileInput}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <div className="flex flex-col items-center gap-2">
          <Upload className="w-10 h-10 text-gray-400" />
          <div className="text-sm text-gray-600">
            <span className="font-semibold text-blue-500">Click to upload</span>{" "}
            or drag and drop
          </div>
          <div className="text-xs text-gray-500">
            Images and videos (max {maxFiles} files)
          </div>
        </div>
      </div>

      {/* File Preview Grid */}
      {value.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {value.map((file, index) => {
            const displayUrl = file.preview || file.url;

            return (
              <div key={index} className="relative group overflow-hidden">
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  {file.isImage && (
                    <img
                      src={displayUrl}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {file.isVideo && (
                    <div className="relative w-full h-full">
                      <video
                        src={displayUrl}
                        className="w-full h-full object-cover"
                      />
                      <FileVideo className="absolute inset-0 m-auto w-12 h-12 text-white opacity-80" />
                    </div>
                  )}
                  {!file.isImage && !file.isVideo && (
                    <FileImage className="w-12 h-12 text-gray-400" />
                  )}
                </div>

                {/* Remove Button */}
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeFile(index)}
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiFileUpload;
